import { apiCweb } from './api-cweb';
import { appLog } from '~src/utils/log';

type getMovementsDataParams = {
  project_id: number;
  tender_id: number;
  ids?: string;
  types?: string[];
  sort?: string;
  lang?: string;
  skip?: number;
  take?: number;
  is_report_sent?: boolean;
  is_ignore?: boolean;
  is_public?: boolean;
};

// Get all movements
export const getMovementsData = async ({
  project_id,
  tender_id,
  ids,
  types,
  sort,
  lang,
  skip,
  take,
  is_report_sent,
  is_ignore,
  is_public,
}: getMovementsDataParams) => {
  appLog('getMovementsData #1 -> ', {
    project_id,
    tender_id,
    ids,
    types,
    sort,
    lang,
    skip,
    take,
    is_report_sent,
    is_ignore,
    is_public,
  });

  const res = await apiCweb.get(
    `/projects/${project_id}/tenders/${tender_id}/movements`,
    {
      params: {
        ids,
        types,
        sort,
        lang,
        skip,
        take,
        is_report_sent,
        is_ignore,
        is_public,
      },
    }
  );

  appLog(`getMovementsData #2 -> `, res.data);
  return res.data;
};

// Get all movements vendors
export const getMovementsDataVendors = async ({
  shareId,
  shareKey,
  ids,
  types,
  sort,
  lang,
  skip,
  take,
  is_report_sent,
  is_ignore,
  is_public,
}: {
  shareId: string;
  shareKey: string;
  ids?: string;
  types?: string[];
  sort?: string;
  lang?: string;
  skip?: number;
  take?: number;
  is_report_sent?: boolean;
  is_ignore?: boolean;
  is_public?: boolean;
}) => {
  appLog('getMovementsDataVendors #1 -> ', {
    shareId,
    shareKey,
    ids,
    types,
    sort,
    lang,
    skip,
    take,
    is_report_sent,
    is_ignore,
    is_public,
  });

  const res = await apiCweb.get(`/tender_shares/${shareId}/movements`, {
    headers: {
      'Share-Key': shareKey,
    },
    params: {
      ids,
      types,
      sort,
      lang,
      skip,
      take,
      is_report_sent,
      is_ignore,
      is_public,
    },
  });

  appLog(`getMovementsDataVendors #2 -> `, res.data);
  return res.data;
};

type addCustomMovementParams = {
  project_id: number;
  tender_id: number;
  body: {
    note: string;
    field: string;
    old_value: string;
    new_value: string;
  };
};

// Add custom movements
export const addCustomMovement = async ({
  project_id,
  tender_id,
  body,
}: addCustomMovementParams) => {
  appLog('addCustomMovement #1 -> ', {
    project_id,
    tender_id,
    body,
  });

  const res = await apiCweb.post(
    `/projects/${project_id}/tenders/${tender_id}/custom_movements`,
    {
      note: body.note,
      field: body.field,
      old_value: body.old_value,
      new_value: body.new_value,
    }
  );

  appLog(`addCustomMovement #2 -> `, res.data);
  return res.data;
};

type addCustomToMovementParams = {
  project_id: number;
  tender_id: number;
  movement_id: number;
  body: {
    note: string;
    field: string;
    old_value: string;
    new_value: string;
  };
};

// Update movement flags (ignore/public)
export const updateMovementFlags = async ({
  project_id,
  tender_id,
  movement_id,
  action,
}: {
  project_id: number;
  tender_id: number;
  movement_id: number;
  action:
    | 'set_ignore_on'
    | 'set_ignore_off'
    | 'set_public_on'
    | 'set_public_off';
}) => {
  appLog('updateMovementFlags #1 -> ', {
    project_id,
    tender_id,
    movement_id,
    action,
  });

  const res = await apiCweb.put(
    `/projects/${project_id}/tenders/${tender_id}/movements/${movement_id}`,
    {},
    {
      params: { action },
    }
  );

  appLog(`updateMovementFlags #2 -> `, res?.data);
  return res?.data;
};

// Add custom TO movements
export const addCustomToMovement = async ({
  project_id,
  tender_id,
  movement_id,
  body,
}: addCustomToMovementParams) => {
  appLog('addCustomToMovement #1 -> ', {
    project_id,
    tender_id,
    body,
  });

  const res = await apiCweb.post(
    `/projects/${project_id}/tenders/${tender_id}/movements/${movement_id}/custom`,
    {
      note: body.note,
      field: body.field,
      old_value: body.old_value,
      new_value: body.new_value,
    }
  );

  appLog(`addCustomToMovement #2 -> `, res.data);
  return res.data;
};

// CORRECTION LETTER API
// Get lastest correction letter
export const getCurrentCorrectionLetter = async ({ project_id, tender_id }) => {
  appLog('getCurrentCorrelationLetter #1 -> ', {
    project_id,
    tender_id,
  });

  const res = await apiCweb.get(
    `/projects/${project_id}/tenders/${tender_id}/current_correction_letter`
  );

  appLog(`getCurrentCorrelationLetter #2 -> `, res.data);
  return res.data;
};

// Get ALL correction letters
export const getAllCorrectionLetters = async ({ project_id, tender_id }) => {
  appLog('getAllCorrectionLetters #1 -> ', {
    project_id,
    tender_id,
  });

  const res = await apiCweb.get(
    `/projects/${project_id}/tenders/${tender_id}/correction_letters`
  );

  appLog(`getAllCorrectionLetters #2 -> `, res.data);
  return res.data;
};

// Get latest correction letter HTML raw preview
export const getCurrentCorrectionLetterPreview = async ({
  project_id,
  tender_id,
  lang,
}) => {
  appLog('getCurrentCorrectionLetterPreview #1 -> ', {
    project_id,
    tender_id,
    lang,
  });

  const res = await apiCweb.get(
    `/projects/${project_id}/tenders/${tender_id}/current_correction_letter/email_preview_as_html`,
    { params: { lang } }
  );

  appLog(`getCurrentCorrectionLetterPreview #2 -> `, res.data);
  return res.data;
};

// Get latest correction letter HTML raw Modification Report preview
export const getCurrentCorrectionLetterReportPreview = async ({
  project_id,
  tender_id,
  lang,
}) => {
  appLog('getCurrentCorrectionLetterReportPreview #1 -> ', {
    project_id,
    tender_id,
    lang,
  });

  const res = await apiCweb.get(
    `/projects/${project_id}/tenders/${tender_id}/current_correction_letter/modification_report_preview_as_html`,
    { params: { lang } }
  );

  appLog(`getCurrentCorrectionLetterReportPreview #2 -> `, res.data);
  return res.data;
};

// Update correlation letter
export interface updateCurrentCorrectionLetteParams {
  projectId: number;
  tenderId: number;
  body: {
    title: string;
    description: string;
  };
}

export const updateCurrentCorrectionLetter = async ({
  projectId,
  tenderId,
  body,
}: updateCurrentCorrectionLetteParams) => {
  appLog('updateCurrentCorrectionLetter #1 -> ', {
    projectId,
    tenderId,
    body,
  });

  const res = await apiCweb.put(
    `/projects/${projectId}/tenders/${tenderId}/current_correction_letter`,
    body,
    { params: { action: 'update' } }
  );

  appLog(`updateCurrentCorrectionLetter #2 -> `, res.data);
  return res.data;
};

// release correction letter
export const releaseCurrentCorrectionLetter = async ({
  projectId,
  tenderId,
  lang,
}) => {
  appLog('releaseCurrentCorrectionLetter #1 -> ', {
    projectId,
    tenderId,
    lang,
  });

  const res = await apiCweb.put(
    `/projects/${projectId}/tenders/${tenderId}/current_correction_letter`,
    {},
    { params: { action: 'release', lang } }
  );

  appLog(`releaseCurrentCorrectionLetter #2 -> `, res.data);
  return res.data;
};

// MANAGE items for Correlation items
// Manage Movements for correction letter
export interface manageCurrentCorrectionLetterMovementsParams {
  projectId: number;
  tenderId: number;
  action: 'add_movement' | 'remove_movement' | 'replace_movement';
  body: {
    movement_ids: number[];
  };
}

export const manageCurrentCorrectionLetterMovements = async ({
  projectId,
  tenderId,
  action,
  body,
}: manageCurrentCorrectionLetterMovementsParams) => {
  appLog('manageCurrentCorrectionLetterMovements #1 -> ', {
    projectId,
    tenderId,
    action,
    body,
  });

  const res = await apiCweb.put(
    `/projects/${projectId}/tenders/${tenderId}/current_correction_letter/movements`,
    body,
    { params: { action } }
  );

  appLog(`manageCurrentCorrectionLetterMovements #2 -> `, res.data);
  return res.data;
};

// Manage Questions for correction letter
export interface manageCurrentCorrectionLetterQuestionsParams {
  projectId: number;
  tenderId: number;
  action: 'add_question' | 'remove_question' | 'replace_question';
  body: {
    question_ids: number[];
  };
}

export const manageCurrentCorrectionLetterQuestions = async ({
  projectId,
  tenderId,
  action,
  body,
}: manageCurrentCorrectionLetterQuestionsParams) => {
  appLog('manageCurrentCorrectionLetterQuestions #1 -> ', {
    projectId,
    tenderId,
    action,
    body,
  });

  const res = await apiCweb.put(
    `/projects/${projectId}/tenders/${tenderId}/current_correction_letter/questions`,
    body,
    { params: { action } }
  );

  appLog(`manageCurrentCorrectionLetterQuestions #2 -> `, res.data);
  return res.data;
};

export type getCorrectionLetterAttachmentDownloadUrlParams = {
  project_id: number;
  tender_id: number;
  tender_correction_letter_id: number;
  file_guid: string;
};

export const getCorrectionLetterAttachmentDownloadUrl = async ({
  project_id,
  tender_id,
  tender_correction_letter_id,
  file_guid,
}: getCorrectionLetterAttachmentDownloadUrlParams) => {
  appLog('getCorrectionLetterAttachmentDownloadUrl #1 -> ', {
    project_id,
    tender_id,
    tender_correction_letter_id,
    file_guid,
  });

  const res = await apiCweb.head(
    `/projects/${project_id}/tenders/${tender_id}/correction_letters/${tender_correction_letter_id}/attachments/${file_guid}/location`
  );

  const location =
    (res.headers?.location as string) ||
    (res.headers?.Location as string) ||
    '';

  appLog(`getCorrectionLetterAttachmentDownloadUrl #2 -> `, {
    status: res.status,
    location,
  });

  return location;
};

export type deleteCurrentCorrectionLetterAttachmentParams = {
  project_id: number;
  tender_id: number;
  file_guid: string;
};

export const deleteCurrentCorrectionLetterAttachment = async ({
  project_id,
  tender_id,
  file_guid,
}: deleteCurrentCorrectionLetterAttachmentParams) => {
  appLog('deleteCurrentCorrectionLetterAttachment #1 -> ', {
    project_id,
    tender_id,
    file_guid,
  });

  const res = await apiCweb.delete(
    `/projects/${project_id}/tenders/${tender_id}/current_correction_letter/attachments/${file_guid}`
  );

  appLog(`deleteCurrentCorrectionLetterAttachment #2 -> `, res.status);
  return res.status;
};

// ======= INSTANT MESSAGE APIs =======
export type InstantMessageAttachment = {
  file_guid: string;
  name: string;
};

export type InstantMessage = {
  id: number;
  title: string;
  message: string;
  created_by_full_name: string;
  created_at: number;
  attachments: InstantMessageAttachment[];
  reference?: string;
  serial_number?: string;
  sent_at?: number;
};

/**
 * Get list of sent instant messages
 */
export const getInstantMessages = async ({
  projectId,
  tenderId,
}: {
  projectId: number;
  tenderId: number;
}): Promise<InstantMessage[]> => {
  appLog('getInstantMessages #1 -> ', { projectId, tenderId });

  const res = await apiCweb.get(
    `/projects/${projectId}/tenders/${tenderId}/instant_messages`
  );

  appLog(`getInstantMessages #2 -> `, res.data);
  return res.data;
};

/**
 * Get S3 download location for a sent message attachment
 */
export const getInstantMessageAttachmentLocation = async ({
  projectId,
  tenderId,
  instantMessageId,
  fileGuid,
}: {
  projectId: number;
  tenderId: number;
  instantMessageId: number;
  fileGuid: string;
}) => {
  appLog('getInstantMessageAttachmentLocation #1 -> ', {
    projectId,
    tenderId,
    instantMessageId,
    fileGuid,
  });

  const res = await apiCweb.head(
    `/projects/${projectId}/tenders/${tenderId}/instant_messages/${instantMessageId}/attachments/${fileGuid}/location`
  );

  const location =
    (res.headers?.location as string) ||
    (res.headers?.Location as string) ||
    '';

  appLog(`getInstantMessageAttachmentLocation #2 -> `, {
    status: res.status,
    location,
  });
  return location;
};

/**
 * Create a new draft instant message
 * Returns the draft ID
 */
export const createInstantMessageDraft = async ({
  projectId,
  tenderId,
}: {
  projectId: number;
  tenderId: number;
}): Promise<{ instant_message_draft_id: number }> => {
  appLog('createInstantMessageDraft #1 -> ', { projectId, tenderId });

  const res = await apiCweb.post(
    `/projects/${projectId}/tenders/${tenderId}/instant_messages/drafts`,
    {}
  );

  appLog(`createInstantMessageDraft #2 -> `, res.data);
  return res.data;
};

/**
 * Get details of a specific instant message draft
 */
export const getInstantMessageDraft = async ({
  projectId,
  tenderId,
  draftId,
}: {
  projectId: number;
  tenderId: number;
  draftId: number;
}): Promise<InstantMessage> => {
  appLog('getInstantMessageDraft #1 -> ', { projectId, tenderId, draftId });

  const res = await apiCweb.get(
    `/projects/${projectId}/tenders/${tenderId}/instant_messages/drafts/${draftId}`
  );

  appLog(`getInstantMessageDraft #2 -> `, res.data);
  return res.data;
};

/**
 * Send the instant message draft
 * Converts draft to sent message and notifies recipients
 */
export interface SendInstantMessageParams {
  projectId: number;
  tenderId: number;
  draftId: number;
  body: {
    title: string;
    message: string;
    reference: string;
  };
}

export const sendInstantMessageDraft = async ({
  projectId,
  tenderId,
  draftId,
  body,
}: SendInstantMessageParams) => {
  appLog('sendInstantMessageDraft #1 -> ', {
    projectId,
    tenderId,
    draftId,
    body,
  });

  const res = await apiCweb.put(
    `/projects/${projectId}/tenders/${tenderId}/instant_messages/drafts/${draftId}`,
    body,
    { params: { action: 'send' } }
  );

  appLog(`sendInstantMessageDraft #2 -> `, res.status);
  return res.status;
};

/**
 * Remove an attachment from a draft
 */
export const deleteInstantMessageDraftAttachment = async ({
  projectId,
  tenderId,
  draftId,
  fileGuid,
}: {
  projectId: number;
  tenderId: number;
  draftId: number;
  fileGuid: string;
}) => {
  appLog('deleteInstantMessageDraftAttachment #1 -> ', {
    projectId,
    tenderId,
    draftId,
    fileGuid,
  });

  const res = await apiCweb.delete(
    `/projects/${projectId}/tenders/${tenderId}/instant_messages/drafts/${draftId}/attachments/${fileGuid}`
  );

  appLog(`deleteInstantMessageDraftAttachment #2 -> `, res.status);
  return res.status;
};

/**
 * Get S3 download location for a draft attachment
 */
export const getInstantMessageDraftAttachmentLocation = async ({
  projectId,
  tenderId,
  draftId,
  fileGuid,
}: {
  projectId: number;
  tenderId: number;
  draftId: number;
  fileGuid: string;
}) => {
  appLog('getInstantMessageDraftAttachmentLocation #1 -> ', {
    projectId,
    tenderId,
    draftId,
    fileGuid,
  });

  const res = await apiCweb.head(
    `/projects/${projectId}/tenders/${tenderId}/instant_messages/drafts/${draftId}/attachments/${fileGuid}/location`
  );

  const location =
    (res.headers?.location as string) ||
    (res.headers?.Location as string) ||
    '';

  appLog(`getInstantMessageDraftAttachmentLocation #2 -> `, {
    status: res.status,
    location,
  });
  return location;
};

/**
 * Get list of all instant message drafts
 */
export const getInstantMessageDrafts = async ({
  projectId,
  tenderId,
}: {
  projectId: number;
  tenderId: number;
}): Promise<InstantMessage[]> => {
  appLog('getInstantMessageDrafts #1 -> ', { projectId, tenderId });

  const res = await apiCweb.get(
    `/projects/${projectId}/tenders/${tenderId}/instant_messages/drafts`
  );

  appLog(`getInstantMessageDrafts #2 -> `, res.data);
  return res.data;
};

/**
 * Update an existing instant message draft (content/title)
 */
export const updateInstantMessageDraft = async ({
  projectId,
  tenderId,
  draftId,
  body,
}: SendInstantMessageParams) => {
  appLog('updateInstantMessageDraft #1 -> ', {
    projectId,
    tenderId,
    draftId,
    body,
  });

  const res = await apiCweb.put(
    `/projects/${projectId}/tenders/${tenderId}/instant_messages/drafts/${draftId}`,
    body,
    { params: { action: 'update' } }
  );

  appLog(`updateInstantMessageDraft #2 -> `, res.status);
  return res.status;
};

/**
 * Get list of all Messages (letters / instant letters)
 */
export const getAllMessages = async ({
  projectId,
  tenderId,
  type,
  reference,
  skip,
  take,
  sort,
}: {
  projectId: number;
  tenderId: number;
  type?: number[];
  reference?: string;
  skip?: number;
  take?: number;
  sort?: string;
}): Promise<InstantMessage[]> => {
  appLog('getAllMessages #1 -> ', { projectId, tenderId, type, reference });

  const res = await apiCweb.get(
    `/projects/${projectId}/tenders/${tenderId}/messages`,
    {
      params: {
        type: type?.join(','),
        reference,
        skip,
        take,
        sort,
      },
    }
  );

  appLog(`getAllMessages #2 -> `, res.data);
  return res.data;
};

/**
 * Get a single correction letter by ID
 */
export const getCorrectionLetter = async ({
  projectId,
  tenderId,
  correctionLetterId,
}: {
  projectId: number;
  tenderId: number;
  correctionLetterId: number;
}) => {
  appLog('getCorrectionLetter #1 -> ', {
    projectId,
    tenderId,
    correctionLetterId,
  });

  const res = await apiCweb.get(
    `/projects/${projectId}/tenders/${tenderId}/correction_letters/${correctionLetterId}`
  );

  appLog(`getCorrectionLetter #2 -> `, res.data);
  return res.data;
};

/**
 * Get a single instant message (sent) by its ID
 */
export const getInstantMessage = async ({
  projectId,
  tenderId,
  instantMessageId,
}: {
  projectId: number;
  tenderId: number;
  instantMessageId: number;
}) => {
  appLog('getInstantMessage #1 -> ', {
    projectId,
    tenderId,
    instantMessageId,
  });

  const res = await apiCweb.get(
    `/projects/${projectId}/tenders/${tenderId}/instant_messages/${instantMessageId}`
  );

  appLog(`getInstantMessage #2 -> `, res.data);
  return res.data;
};

/**
 * Get list of all Messages (letters / instant letters) for Vendors
 */
export const getAllMessagesVendors = async ({
  shareId,
  shareKey,
  type,
  skip,
  take,
  sort,
}: {
  shareId: string;
  shareKey: string;
  type?: number[];
  skip?: number;
  take?: number;
  sort?: string;
}): Promise<InstantMessage[]> => {
  appLog('getAllMessagesVendors #1 -> ', { shareId, shareKey, type });

  const res = await apiCweb.get(`/tender_shares/${shareId}/messages`, {
    headers: {
      'Share-Key': shareKey,
    },
    params: {
      type: type?.join(','),
      skip,
      take,
      sort,
    },
  });

  appLog(`getAllMessagesVendors #2 -> `, res.data);
  return res.data;
};

export type GetCorrectionLetterAttachmentVendorParams = {
  shareId: string;
  shareKey: string;
  tender_correction_letter_id: number;
  file_guid: string;
};

export const getCorrectionLetterAttachmentDownloadUrlVendor = async ({
  shareId,
  shareKey,
  tender_correction_letter_id,
  file_guid,
}: GetCorrectionLetterAttachmentVendorParams) => {
  appLog('getCorrectionLetterAttachmentDownloadUrlVendor #1 -> ', {
    shareId,
    tender_correction_letter_id,
    file_guid,
  });

  const res = await apiCweb.head(
    `/tender_shares/${shareId}/correction_letters/${tender_correction_letter_id}/attachments/${file_guid}/location`,
    {
      headers: {
        'Share-Key': shareKey,
      },
    }
  );

  const location =
    (res.headers?.location as string) ||
    (res.headers?.Location as string) ||
    '';

  appLog(`getCorrectionLetterAttachmentDownloadUrlVendor #2 -> `, {
    status: res.status,
    location,
  });

  return location;
};

export type GetInstantMessageAttachmentVendorParams = {
  shareId: string;
  shareKey: string;
  instant_message_id: number;
  file_guid: string;
};

export const getInstantMessageAttachmentLocationVendor = async ({
  shareId,
  shareKey,
  instant_message_id,
  file_guid,
}: GetInstantMessageAttachmentVendorParams) => {
  appLog('getInstantMessageAttachmentLocationVendor #1 -> ', {
    shareId,
    instant_message_id,
    file_guid,
  });

  const res = await apiCweb.head(
    `/tender_shares/${shareId}/instant_messages/${instant_message_id}/attachments/${file_guid}/location`,
    {
      headers: {
        'Share-Key': shareKey,
      },
    }
  );

  const location =
    (res.headers?.location as string) ||
    (res.headers?.Location as string) ||
    '';

  appLog(`getInstantMessageAttachmentLocationVendor #2 -> `, {
    status: res.status,
    location,
  });

  return location;
};

/**
 * Get a single correction letter by ID for Vendors
 */
export const getCorrectionLetterVendor = async ({
  shareId,
  shareKey,
  correctionLetterId,
}: {
  shareId: string;
  shareKey: string;
  correctionLetterId: number;
}) => {
  appLog('getCorrectionLetterVendor #1 -> ', { shareId, correctionLetterId });

  const res = await apiCweb.get(
    `/tender_shares/${shareId}/correction_letters/${correctionLetterId}`,
    {
      headers: { 'Share-Key': shareKey },
    }
  );

  appLog(`getCorrectionLetterVendor #2 -> `, res.data);
  return res.data;
};

/**
 * Get a single instant message (sent) by its ID for Vendors
 */
export const getInstantMessageVendor = async ({
  shareId,
  shareKey,
  instantMessageId,
}: {
  shareId: string;
  shareKey: string;
  instantMessageId: number;
}) => {
  appLog('getInstantMessageVendor #1 -> ', { shareId, instantMessageId });

  const res = await apiCweb.get(
    `/tender_shares/${shareId}/instant_messages/${instantMessageId}`,
    {
      headers: { 'Share-Key': shareKey },
    }
  );

  appLog(`getInstantMessageVendor #2 -> `, res.data);
  return res.data;
};
// Get SENT correction letter HTML raw preview
export const getSentCorrectionLetterEmailPreviewHtml = async ({
  project_id,
  tender_id,
  tender_correction_letter_id,
  lang,
}: {
  project_id: number;
  tender_id: number;
  tender_correction_letter_id: number;
  lang?: string;
}) => {
  const res = await apiCweb.get(
    `/projects/${project_id}/tenders/${tender_id}/correction_letters/${tender_correction_letter_id}/email_preview_as_html`,
    { params: { lang } }
  );
  return res.data;
};

// Get DRAFT instant message HTML raw preview
export const getDraftInstantMessageEmailPreviewHtml = async ({
  projectId,
  tenderId,
  draftId,
  lang,
}: {
  projectId: number;
  tenderId: number;
  draftId: number;
  lang?: string;
}) => {
  const res = await apiCweb.get(
    `/projects/${projectId}/tenders/${tenderId}/instant_messages/drafts/${draftId}/email_preview_as_html`,
    { params: { lang } }
  );
  return res.data;
};

// Get SENT instant message HTML raw preview
export const getSentInstantMessageEmailPreviewHtml = async ({
  projectId,
  tenderId,
  instantMessageId,
  lang,
}: {
  projectId: number;
  tenderId: number;
  instantMessageId: number;
  lang?: string;
}) => {
  const res = await apiCweb.get(
    `/projects/${projectId}/tenders/${tenderId}/instant_messages/${instantMessageId}/email_preview_as_html`,
    { params: { lang } }
  );
  return res.data;
};
// Get SENT correction letter HTML raw preview (VENDOR)
export const getSentCorrectionLetterEmailPreviewHtmlVendor = async ({
  shareId,
  shareKey,
  correctionLetterId,
  lang,
}: {
  shareId: string;
  shareKey: string;
  correctionLetterId: number;
  lang?: string;
}) => {
  const res = await apiCweb.get(
    `/tender_shares/${shareId}/correction_letters/${correctionLetterId}/email_preview_as_html`,
    {
      headers: { 'Share-Key': shareKey },
      params: { lang },
    }
  );
  return res.data;
};

// Get SENT instant message HTML raw preview (VENDOR)
export const getSentInstantMessageEmailPreviewHtmlVendor = async ({
  shareId,
  shareKey,
  instantMessageId,
  lang,
}: {
  shareId: string;
  shareKey: string;
  instantMessageId: number;
  lang?: string;
}) => {
  const res = await apiCweb.get(
    `/tender_shares/${shareId}/instant_messages/${instantMessageId}/email_preview_as_html`,
    {
      headers: { 'Share-Key': shareKey },
      params: { lang },
    }
  );
  return res.data;
};

// ==== GLOBAL EVALUATION INSTANT MESSAGES (no contract_id) ====

/**
 * Get a single global evaluation instant message by ID (project side)
 */
export const getGlobalEvaluationInstantMessage = async ({
  projectId,
  tenderId,
  instantMessageId,
}: {
  projectId: number;
  tenderId: number;
  instantMessageId: number;
}): Promise<InstantMessage> => {
  appLog('getGlobalEvaluationInstantMessage #1 ->', {
    projectId,
    tenderId,
    instantMessageId,
  });

  const res = await apiCweb.get<InstantMessage>(
    `/projects/${projectId}/tenders/${tenderId}/evaluation_instant_message/${instantMessageId}`
  );

  appLog('getGlobalEvaluationInstantMessage #2 ->', res.data);
  return res.data;
};

/**
 * Get email HTML preview of a global evaluation instant message (project side)
 */
export const getGlobalEvaluationInstantMessageEmailPreview = async ({
  projectId,
  tenderId,
  instantMessageId,
  lang,
}: {
  projectId: number;
  tenderId: number;
  instantMessageId: number;
  lang?: string;
}): Promise<string> => {
  appLog('getGlobalEvaluationInstantMessageEmailPreview #1 ->', {
    projectId,
    tenderId,
    instantMessageId,
    lang,
  });

  const res = await apiCweb.get<string>(
    `/projects/${projectId}/tenders/${tenderId}/evaluation_instant_message/${instantMessageId}/email_preview_as_html`,
    { params: { lang } }
  );

  appLog('getGlobalEvaluationInstantMessageEmailPreview #2 ->', res.status);
  return res.data;
};

/**
 * Get Amazon S3 location of a global evaluation instant message attachment (project side)
 */
export const getGlobalEvaluationInstantMessageAttachmentLocation = async ({
  projectId,
  tenderId,
  instantMessageId,
  fileGuid,
}: {
  projectId: number;
  tenderId: number;
  instantMessageId: number;
  fileGuid: string;
}): Promise<string> => {
  appLog('getGlobalEvaluationInstantMessageAttachmentLocation #1 ->', {
    projectId,
    tenderId,
    instantMessageId,
    fileGuid,
  });

  const res = await apiCweb.head(
    `/projects/${projectId}/tenders/${tenderId}/evaluation_instant_message/${instantMessageId}/attachments/${fileGuid}/location`
  );

  const location =
    (res.headers?.location as string) ||
    (res.headers?.Location as string) ||
    '';

  appLog('getGlobalEvaluationInstantMessageAttachmentLocation #2 ->', {
    status: res.status,
    location,
  });
  return location;
};

/**
 * Get a single global evaluation instant message by ID (vendor side)
 */
export const getGlobalEvaluationInstantMessageVendor = async ({
  shareId,
  shareKey,
  instantMessageId,
}: {
  shareId: string;
  shareKey: string;
  instantMessageId: number;
}): Promise<InstantMessage> => {
  appLog('getGlobalEvaluationInstantMessageVendor #1 ->', {
    shareId,
    instantMessageId,
  });

  const res = await apiCweb.get<InstantMessage>(
    `/tender_shares/${shareId}/evaluation_instant_message/${instantMessageId}`,
    { headers: { 'Share-Key': shareKey } }
  );

  appLog('getGlobalEvaluationInstantMessageVendor #2 ->', res.data);
  return res.data;
};

/**
 * Get email HTML preview of a global evaluation instant message (vendor side)
 */
export const getGlobalEvaluationInstantMessageEmailPreviewVendor = async ({
  shareId,
  shareKey,
  instantMessageId,
  lang,
}: {
  shareId: string;
  shareKey: string;
  instantMessageId: number;
  lang?: string;
}): Promise<string> => {
  appLog('getGlobalEvaluationInstantMessageEmailPreviewVendor #1 ->', {
    shareId,
    instantMessageId,
    lang,
  });

  const res = await apiCweb.get<string>(
    `/tender_shares/${shareId}/evaluation_instant_message/${instantMessageId}/email_preview_as_html`,
    {
      headers: { 'Share-Key': shareKey },
      params: { lang },
    }
  );

  appLog(
    'getGlobalEvaluationInstantMessageEmailPreviewVendor #2 ->',
    res.status
  );
  return res.data;
};

/**
 * Get Amazon S3 location of a global evaluation instant message attachment (vendor side)
 */
export const getGlobalEvaluationInstantMessageAttachmentLocationVendor = async ({
  shareId,
  shareKey,
  instantMessageId,
  fileGuid,
}: {
  shareId: string;
  shareKey: string;
  instantMessageId: number;
  fileGuid: string;
}): Promise<string> => {
  appLog('getGlobalEvaluationInstantMessageAttachmentLocationVendor #1 ->', {
    shareId,
    instantMessageId,
    fileGuid,
  });

  const res = await apiCweb.head(
    `/tender_shares/${shareId}/evaluation_instant_message/${instantMessageId}/attachments/${fileGuid}/location`,
    { headers: { 'Share-Key': shareKey } }
  );

  const location =
    (res.headers?.location as string) ||
    (res.headers?.Location as string) ||
    '';

  appLog('getGlobalEvaluationInstantMessageAttachmentLocationVendor #2 ->', {
    status: res.status,
    location,
  });
  return location;
};
